import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import Seo from '../../components/Seo';
import { toast } from 'sonner';
import {
  Building2, MapPin, Search, Video, Users, User, Globe, Phone, Mail,
  PlusCircle, Sparkles, Loader2, BadgeCheck, X, Star, MessageSquarePlus, ShieldAlert,
} from 'lucide-react';

const PLACEHOLDER = '/inasw-logo.jpg';
const SAMPLE_PRACTICES_COUNT = 5;
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const Stars = ({ value = 0, size = 'w-4 h-4', onSelect }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} onClick={onSelect ? () => onSelect(n) : undefined}
        className={`${size} ${onSelect ? 'cursor-pointer' : ''} ${n <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        data-testid={onSelect ? `star-select-${n}` : undefined} />
    ))}
  </span>
);

const ReviewsModal = ({ practice, onClose, onPosted }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/private-practices/${practice.practice_id}/reviews`); setData(r.data); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [practice.practice_id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!isAuthenticated) { toast.info('Please sign in to leave a review.'); navigate('/login'); return; }
    if (rating < 1) { toast.error('Please select a star rating.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/private-practices/${practice.practice_id}/reviews`, { rating, comment, reviewer_name: name });
      toast.success('Thank you — your review was posted.');
      setRating(0); setComment(''); setName('');
      await load();
      onPosted && onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post review.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="pp-reviews-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        <h3 className="font-serif text-xl font-bold text-primary">{practice.practice_name}</h3>
        <div className="flex items-center gap-2 mt-1">
          <Stars value={data.average} />
          <span className="text-sm text-gray-500">{data.count ? `${data.average} · ${data.count} review${data.count === 1 ? '' : 's'}` : 'No reviews yet'}</span>
        </div>

        {/* Write a review */}
        <div className="mt-5 border border-gray-100 rounded-xl p-4 bg-gray-50/60">
          <p className="text-sm font-semibold text-primary mb-2">Leave a verified review</p>
          <Stars value={rating} size="w-6 h-6" onSelect={setRating} />
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="mt-3 bg-white" data-testid="pp-review-name" />
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience with this practice…"
            className="mt-2 w-full border rounded-md p-2 text-sm bg-white min-h-[80px]" data-testid="pp-review-comment" />
          <Button onClick={submit} disabled={submitting} className="mt-3 bg-secondary hover:bg-secondary-hover text-white w-full" data-testid="pp-review-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquarePlus className="w-4 h-4 mr-2" />} Post review
          </Button>
        </div>

        {/* Existing reviews */}
        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="text-center py-6 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : data.reviews.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Be the first to review this practice.</p>
          ) : data.reviews.map((r) => (
            <div key={r.review_id} className="border-b border-gray-100 pb-3" data-testid={`pp-review-${r.review_id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800 text-sm flex items-center gap-1">{r.reviewer_name} {r.verified && <BadgeCheck className="w-3.5 h-3.5 text-green-600" />}</span>
                <Stars value={r.rating} size="w-3.5 h-3.5" />
              </div>
              {r.comment && <p className="text-sm text-gray-600 mt-1">{r.comment}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PracticeCard = ({ p }) => {
  const [open, setOpen] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [ratingAvg, setRatingAvg] = useState(p.rating_avg || 0);
  const [ratingCount, setRatingCount] = useState(p.rating_count || 0);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col hover:shadow-lg hover:border-secondary/40 transition-all" data-testid={`pp-card-${p.practice_id}`}>
      <div className="flex items-start gap-3">
        <img src={p.logo_data || PLACEHOLDER} alt={p.practice_name} onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-14 h-14 rounded-xl object-cover border border-gray-100 bg-gray-50 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <h3 className="font-serif font-bold text-primary text-lg leading-tight truncate">{p.practice_name}</h3>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="text-xs">
              {p.practice_type === 'group' ? <><Users className="w-3 h-3 mr-1" /> Group Practice</> : <><User className="w-3 h-3 mr-1" /> Individual</>}
            </Badge>
            {p.telehealth && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 text-xs"><Video className="w-3 h-3 mr-1" /> Telehealth</Badge>}
            {p.in_person && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 text-xs"><Building2 className="w-3 h-3 mr-1" /> In-Person</Badge>}
            {p.accepting_new_clients && <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">Accepting New Clients</Badge>}
          </div>
        </div>
      </div>
      {!p.is_sample && (
        <button onClick={() => setShowReviews(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm hover:opacity-80" data-testid={`pp-reviews-btn-${p.practice_id}`}>
          <Stars value={ratingAvg} size="w-4 h-4" />
          <span className="text-gray-500">{ratingCount ? `${ratingAvg} (${ratingCount})` : 'Be the first to review'}</span>
        </button>
      )}
      {(p.city || p.state) && (
        <p className="text-sm text-gray-500 mt-3 flex items-center gap-1"><MapPin className="w-4 h-4" /> {[p.city, p.state].filter(Boolean).join(', ')}</p>
      )}
      {p.description && <p className="text-sm text-gray-600 mt-2 line-clamp-3">{p.description}</p>}
      {p.specialties?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {p.specialties.slice(0, 5).map((s) => <span key={s} className="text-xs bg-secondary/10 text-secondary rounded-full px-2.5 py-1">{s}</span>)}
        </div>
      )}
      <div className="mt-auto pt-4">
        {p.is_sample ? null : !open ? (
          <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white" onClick={() => setOpen(true)} data-testid={`pp-contact-${p.practice_id}`}>
            View Contact Info
          </Button>
        ) : (
          <div className="space-y-1.5 text-sm">
            {p.contact_name && <p className="text-gray-700 font-medium">{p.contact_name}</p>}
            {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-primary hover:underline"><Mail className="w-4 h-4" /> {p.email}</a>}
            {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-primary hover:underline"><Phone className="w-4 h-4" /> {p.phone}</a>}
            {p.website && <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline"><Globe className="w-4 h-4" /> Website</a>}
          </div>
        )}
      </div>
      {showReviews && !p.is_sample && <ReviewsModal practice={p} onClose={() => setShowReviews(false)} onPosted={() => { setRatingCount((c) => c || 1); }} />}
    </div>
  );
};

const PrivatePracticeDirectory = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [practices, setPractices] = useState([]);
  const [total, setTotal] = useState(0);
  const [sampleInfo, setSampleInfo] = useState({ real_total: 0, sample_count: 0, sample_threshold: 10 });
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ free_launch_mode: true, tiers: [], specialties: [] });
  const [showTiers, setShowTiers] = useState(false);
  const [draft, setDraft] = useState({ search: '', state: '', practice_type: '', specialty: '', telehealth: false, accepting: false });
  const [filters, setFilters] = useState({ search: '', state: '', practice_type: '', specialty: '', telehealth: false, accepting: false });

  useEffect(() => { axios.get(`${API_URL}/private-practices/config`).then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const fetchPractices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API_URL}/private-practices`, { params });
      setPractices(res.data.practices || []);
      setTotal(res.data.total || 0);
      setSampleInfo({ real_total: res.data.real_total || 0, sample_count: res.data.sample_count || 0, sample_threshold: res.data.sample_threshold || 10 });
    } catch (e) { setPractices([]); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchPractices(); }, [fetchPractices]);

  const listBtn = () => {
    if (!isAuthenticated) { toast.info('Please sign in or create a free account to list your practice.'); navigate('/login'); return; }
    navigate('/private-practice/list');
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <Seo
        exactTitle="Social Work Private Practice Directory | INASW"
        description="Find and list social work private practices nationwide — individual therapists and group practices offering telehealth and in-person care. Free to list during launch on I Need A Social Worker."
        path="/private-practice"
      />
      {/* Hero */}
      <section className="relative bg-primary pt-24 pb-14" data-testid="pp-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <p className="inline-flex items-center gap-2 text-secondary font-semibold tracking-widest uppercase text-xs mb-3"><Building2 className="w-4 h-4" /> Private Practice Directory</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">Social Work <span className="text-secondary">Private Practice</span> Directory<sup className="text-secondary text-[0.4em] align-super font-semibold ml-0.5">™</sup></h1>
          <p className="mt-4 text-white/90 text-lg max-w-2xl">Connect with licensed social work therapists and group practices nationwide — telehealth and in-person. {config.free_launch_mode && <span className="font-semibold text-secondary">Free to list during our launch.</span>}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={listBtn} className="bg-secondary hover:bg-secondary-hover text-white h-11 px-6" data-testid="pp-list-your-practice">
              <PlusCircle className="w-5 h-5 mr-2" /> List Your Practice
            </Button>
            <Button variant="outline" onClick={() => setShowTiers(true)} className="bg-white/10 border-white/40 text-white hover:bg-white/20 h-11 px-6" data-testid="pp-view-plans">
              View Plans
            </Button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={draft.search} onChange={(e) => setDraft({ ...draft, search: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && setFilters(draft)} placeholder="Search name, specialty or city…" className="pl-9" data-testid="pp-search" />
          </div>
          <select value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} className="border rounded-md px-3 h-10 text-sm bg-white" data-testid="pp-state">
            <option value="">All states</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={draft.practice_type} onChange={(e) => setDraft({ ...draft, practice_type: e.target.value })} className="border rounded-md px-3 h-10 text-sm bg-white" data-testid="pp-type">
            <option value="">Individual & Group</option>
            <option value="individual">Individual</option>
            <option value="group">Group Practice</option>
          </select>
          <select value={draft.specialty} onChange={(e) => setDraft({ ...draft, specialty: e.target.value })} className="border rounded-md px-3 h-10 text-sm bg-white" data-testid="pp-specialty">
            <option value="">All specialties</option>
            {(config.specialties || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 px-2 whitespace-nowrap"><input type="checkbox" checked={draft.telehealth} onChange={(e) => setDraft({ ...draft, telehealth: e.target.checked })} data-testid="pp-telehealth" /> Telehealth</label>
          <label className="flex items-center gap-2 text-sm text-gray-700 px-2 whitespace-nowrap"><input type="checkbox" checked={draft.accepting} onChange={(e) => setDraft({ ...draft, accepting: e.target.checked })} data-testid="pp-accepting" /> Accepting New Clients</label>
          <Button onClick={() => setFilters(draft)} className="bg-primary hover:bg-primary/90 text-white" data-testid="pp-apply">Search</Button>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isAdmin && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="pp-admin-sunset">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-amber-800">Admin · Starter listings status:</span>{' '}
              {sampleInfo.real_total >= sampleInfo.sample_threshold ? (
                <span className="text-amber-800">{sampleInfo.real_total} real practices listed — the {SAMPLE_PRACTICES_COUNT} sample listings have automatically retired.</span>
              ) : (
                <span className="text-amber-800">{sampleInfo.real_total} real {sampleInfo.real_total === 1 ? 'practice' : 'practices'} listed. <strong>{Math.max(0, sampleInfo.sample_threshold - sampleInfo.real_total)} more</strong> until the {sampleInfo.sample_count || SAMPLE_PRACTICES_COUNT} sample listings auto-retire (at {sampleInfo.sample_threshold}).</span>
              )}
            </div>
          </div>
        )}
        <p className="text-sm text-gray-500 mb-4" data-testid="pp-result-count">{total} {total === 1 ? 'practice' : 'practices'} listed</p>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : practices.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center" data-testid="pp-empty">
            <Sparkles className="w-10 h-10 text-secondary mx-auto mb-3" />
            <h3 className="font-serif text-xl font-bold text-primary">Be one of the first listed</h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">No practices match yet. List your private practice free during our launch and reach clients and referring social workers nationwide.</p>
            <Button onClick={listBtn} className="mt-5 bg-secondary hover:bg-secondary-hover text-white"><PlusCircle className="w-5 h-5 mr-2" /> List Your Practice</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {practices.map((p) => <PracticeCard key={p.practice_id} p={p} />)}
          </div>
        )}
      </section>

      {/* Plans dialog */}
      {showTiers && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowTiers(false)} data-testid="pp-tiers-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowTiers(false)} className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            <h3 className="font-serif text-2xl font-bold text-primary text-center">Listing Plans</h3>
            {config.free_launch_mode && (
              <div className="mt-3 mb-5 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <BadgeCheck className="w-6 h-6 text-green-600 mx-auto mb-1" />
                <p className="font-bold text-green-800">Free during launch</p>
                <p className="text-sm text-green-700">List your practice at no cost right now. Paid upgrades are coming soon.</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {config.tiers.map((t) => (
                <div key={t.id} className="border border-gray-200 rounded-xl p-5 flex flex-col" data-testid={`pp-tier-${t.id}`}>
                  <h4 className="font-bold text-primary">{t.name}</h4>
                  <p className="text-2xl font-black text-primary mt-1">${t.price}<span className="text-sm font-normal text-gray-400">/mo</span></p>
                  <ul className="text-sm text-gray-600 mt-3 space-y-1 flex-1">{t.features.map((f) => <li key={f}>• {f}</li>)}</ul>
                  <Button disabled onClick={() => toast.info('Paid plans are coming soon — listing is free during launch.')} className="mt-4 bg-gray-200 text-gray-500 cursor-not-allowed" data-testid={`pp-tier-buy-${t.id}`}>
                    Coming Soon
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-center mt-5">
              <Button onClick={() => { setShowTiers(false); listBtn(); }} className="bg-secondary hover:bg-secondary-hover text-white">List Free Now</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivatePracticeDirectory;
